import type { ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { marketingUrl } from "../config/marketing";
import { SUPPORT_EMAIL } from "../lib/portalCopy";
import { accountPreviewEnabled } from "../dev/preview";
import { IconCard, IconDownload, IconGlobe, IconHelp, IconHome, IconLock, IconLogout } from "./icons";

const PAGE_TITLES: Record<string, { title: string; lead: string }> = {
  "/": { title: "Übersicht", lead: "Mitgliedschaft, Geräte und Ergänzungen auf einen Blick." },
  "/billing": { title: "Mitgliedschaft", lead: "Tarif, Abrechnung und Zusatzleistungen." },
  "/billing/success": { title: "Zahlung", lead: "Die Freischaltung folgt, sobald die Bestätigung da ist." },
  "/billing/cancel": { title: "Checkout", lead: "Es wurde nichts berechnet." },
  "/downloads": { title: "Downloads", lead: "Installer für Windows und macOS, App Store für das iPhone." },
  "/security": { title: "Konto & Sicherheit", lead: "Anmeldung, Passwort und Konto löschen." },
};

const NAV: Array<{ to: string; label: string; short: string; icon: ReactNode; end?: boolean }> = [
  { to: "/", label: "Übersicht", short: "Übersicht", icon: <IconHome />, end: true },
  { to: "/billing", label: "Mitgliedschaft", short: "Tarif", icon: <IconCard /> },
  { to: "/downloads", label: "Downloads", short: "Downloads", icon: <IconDownload /> },
  { to: "/security", label: "Konto & Sicherheit", short: "Konto", icon: <IconLock /> },
];

export function Emblem() {
  return (
    <span className="emblem" aria-hidden="true">
      <img src="/images/brand/c-logo-klein.svg" alt="" />
    </span>
  );
}

export function AccountShell() {
  const { email, loggedIn, logout, ready } = useAccountAuth();
  const location = useLocation();
  const page = PAGE_TITLES[location.pathname] ?? { title: "Konto", lead: "" };

  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail__top">
          <a className="brand" href={marketingUrl("/")}>
            <Emblem />
            <span className="brand__name">
              Capital Pilot
              <small>Konto</small>
            </span>
          </a>
          {ready && loggedIn ? (
            <button type="button" className="rail__logout-compact" onClick={() => void logout()} aria-label="Abmelden">
              <IconLogout />
            </button>
          ) : null}
        </div>

        <nav className="rail__nav" aria-label="Konto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? "rail__link is-active" : "rail__link")}
            >
              {item.icon}
              <span className="rail__label">{item.label}</span>
              <span className="rail__label-short" aria-hidden="true">{item.short}</span>
            </NavLink>
          ))}
        </nav>

        <nav className="rail__nav rail__nav--foot" aria-label="Hilfe">
          <a className="rail__link" href={marketingUrl("/")}>
            <IconGlobe />
            <span className="rail__label">Website</span>
          </a>
          <a className="rail__link" href={`mailto:${SUPPORT_EMAIL}`}>
            <IconHelp />
            <span className="rail__label">Support</span>
          </a>
        </nav>

        {ready && loggedIn ? (
          <div className="rail__profile">
            <span className="rail__email" title={email ?? undefined}>{email}</span>
            <button type="button" className="rail__logout" onClick={() => void logout()}>
              <IconLogout />
              Abmelden
            </button>
          </div>
        ) : null}
      </aside>

      <div className="workspace">
        <main className="workspace__main">
          {accountPreviewEnabled() ? (
            <p className="preview-strip">Lokale Vorschau mit Beispieldaten. Anmeldung und Abrechnung sind hier nicht verbunden.</p>
          ) : null}
          <header className="page-head">
            <h1>{page.title}</h1>
            {page.lead ? <p>{page.lead}</p> : null}
          </header>
          <Outlet />
        </main>
        <footer className="workspace__foot">
          <a href={marketingUrl("/impressum")}>Impressum</a>
          <a href={marketingUrl("/datenschutz")}>Datenschutz</a>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </footer>
      </div>
    </div>
  );
}
